"use client"

import React, { useState, useMemo, useEffect, useRef, useCallback } from "react"
import { useFilterSearchHomes, FilterSearchHomesResponse } from "../hooks/filter-search-homes.hook"
import { useRouter, usePathname } from "next/navigation"
import { useCurrentSearchParams } from "@/shared/hooks/use-current-search-params"
import { GetHomeOwnerQueryParamsType } from "../schema/home/query-params.schema"
import { buildUrl } from "@/shared/lib/utils"
import { Input } from "@/shared/components/ui/input"
import { Check, ChevronsUpDown, X } from "lucide-react"
import { cn } from "@/shared/lib/utils"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/shared/components/ui/popover"
import { useRegionId } from "@/shared/hooks/use-region-id"
import { useRegion } from "@/shared/hooks/use-region"
import { useSession } from "@/features/auth/context/session.context"
import {
  shouldMaskHomeownerData,
  maskHomeownerLastName,
  maskHomeownerPhone,
  maskHomeownerAddress,
} from "@/shared/lib/utils/homeowner-privacy.utils"
import { getDisplayAddressValue, isUnknownLocationValue } from "@/features/homeowners/lib/unknown-location"

interface HomeOption {
  value: string
  label: string
  fullname?: string
  phone?: string
  address?: string
  userId?: number
}

function FilterSearchHomeInputCleanV2() {
  // Core state - only what we absolutely need
  const [inputValue, setInputValue] = useState("")
  const [isDropdownOpen, setIsDropdownOpen] = useState(false)
  
  const { regionId, isLoading: regionLoading } = useRegionId()
  const { regionSlug } = useRegion()
  const { session } = useSession()
  const shouldFetchHomes = (regionSlug != null && regionSlug.trim() !== '') || (!regionLoading && regionId != null)
  const { data: homesData, isLoading: isLoadingHomes } = useFilterSearchHomes(
    regionId ?? undefined,
    false,
    { enabled: shouldFetchHomes, regionSlug: regionSlug ?? undefined }
  )
  const router = useRouter()
  const pathname = usePathname()
  const inputRef = useRef<HTMLInputElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  
  // Get current search params to preserve filters
  const currentParams = useCurrentSearchParams<GetHomeOwnerQueryParamsType>([
    "overallStatus",
    "reliefStatus",
    "rebuildingStatus",
    "goodwillStatus",
    "roofing",
    "kindOfRoofWork",
    "medicalNeeds",
    "homeownerAge",
    "isSingleParent",
    "numChildrenUnder18",
    "isFirstResponder",
    "isVeteran",
    "femaStatus",
    "sbaStatus",
    "residenceType",
    "structureType",
    "assessmentCompleted",
    "addedFrom",
    "addedTo",
    "search"
  ])

  // Transform homes data into options
  const homeOptions: HomeOption[] = useMemo(() => {
    if (!homesData) return []

    const response = homesData as FilterSearchHomesResponse
    if (!response.success || !response.data) return []

    return response.data.map((home: any) => ({
      value: home.id.toString(),
      label: `${home.homeowner?.user?.fullname || 'Unknown'} - ${getDisplayAddressValue(home.address)}${home.homeowner?.user?.phone ? ` (${home.homeowner.user.phone})` : ''}`,
      fullname: home.homeowner?.user?.fullname,
      phone: home.homeowner?.user?.phone,
      address: home.address,
      userId: home.homeowner?.user?.id,
    }))
  }, [homesData])

  const getDisplayLabel = useCallback(
    (option: HomeOption) => {
      const mask = shouldMaskHomeownerData(session?.role, session?.sub, option.userId)
      if (!mask || !option.fullname) return option.label
      const name = maskHomeownerLastName(option.fullname)
      const addr = isUnknownLocationValue(option.address)
        ? getDisplayAddressValue(option.address)
        : maskHomeownerAddress(option.address)
      const ph = option.phone ? ` (${maskHomeownerPhone(option.phone)})` : ''
      return `${name} - ${addr}${ph}`
    },
    [session?.role, session?.sub]
  )

  // Filter options based on input value
  const filteredOptions = useMemo(() => {
    if (!inputValue.trim()) return homeOptions
    
    const searchLower = inputValue.toLowerCase()
    return homeOptions.filter(option =>
      option.label.toLowerCase().includes(searchLower)
    )
  }, [homeOptions, inputValue])

  // Initialize input value from URL params
  useEffect(() => {
    const searchParam = currentParams.search || ""
    
    // If search param is a home ID, find the corresponding label
    if (searchParam && !isNaN(Number(searchParam))) {
      const selectedOption = homeOptions.find(option => option.value === searchParam)
      if (selectedOption) {
        setInputValue(getDisplayLabel(selectedOption))
      } else {
        setInputValue(searchParam) // Fallback to showing the ID if option not found
      }
    } else {
      setInputValue(searchParam) // Show text search as-is
    }
  }, [currentParams.search, homeOptions, getDisplayLabel])

  // Handle input change
  const handleInputChange = (value: string) => {
    setInputValue(value)
    // Open dropdown when user starts typing
    if (value.trim() && !isDropdownOpen) {
      setIsDropdownOpen(true)
    }
  }

  // Handle input focus - open dropdown
  const handleInputFocus = () => {
    setIsDropdownOpen(true)
  }

  // Handle input click - open dropdown
  const handleInputClick = () => {
    setIsDropdownOpen(true)
  }

  // Handle option selection from dropdown
  const handleOptionSelect = (option: HomeOption) => {
    setInputValue(getDisplayLabel(option))
    setIsDropdownOpen(false)
    
    // Update URL with selected home ID
    const updatedParams = {
      ...currentParams,
      search: option.value, // Use home ID for filtering
      page: 1,
      tab: "filter-search"
    }
    
    const url = buildUrl(pathname, updatedParams)
    router.push(url)
  }

  // Handle search (Enter key)
  const handleSearch = () => {
    if (!inputValue.trim()) return
    
    setIsDropdownOpen(false)
    
    // Update URL with search text
    const updatedParams = {
      ...currentParams,
      search: inputValue.trim(),
      page: 1,
      tab: "filter-search"
    }
    
    const url = buildUrl(pathname, updatedParams)
    router.push(url)
  }

  // Handle clear
  const handleClear = () => {
    setInputValue("")
    setIsDropdownOpen(false)
    
    // Clear search from URL
    const updatedParams = {
      ...currentParams,
      search: undefined,
      page: 1,
      tab: "filter-search"
    }
    
    const url = buildUrl(pathname, updatedParams)
    router.push(url)
  }

  // Handle key down
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      handleSearch()
    } else if (e.key === 'Escape') {
      setIsDropdownOpen(false)
      inputRef.current?.blur()
    }
  }

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (!containerRef.current) return
      
      const target = event.target as Node
      const container = containerRef.current
      
      // Check if the click is outside the container
      if (!container.contains(target)) {
        // Additional check: if clicking on scrollbar or within popover content, don't close
        const targetElement = target as Element
        
        // Check if clicking on scrollbar elements
        if (targetElement && (
          targetElement.classList.contains('scrollbar-thumb') ||
          targetElement.classList.contains('scrollbar-track') ||
          targetElement.closest('[data-radix-scroll-area-viewport]') ||
          targetElement.closest('[data-radix-popper-content-wrapper]')
        )) {
          return // Don't close if clicking on scrollbar or popover content
        }
        
        setIsDropdownOpen(false)
      }
    }

    if (isDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside)
      return () => document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isDropdownOpen])

  return (
    <div ref={containerRef} className="relative w-full">
      <Popover 
        open={isDropdownOpen} 
        onOpenChange={setIsDropdownOpen}
      >
        <PopoverTrigger asChild>
          <div className="relative">
            <Input
              ref={inputRef}
              value={inputValue}
              onChange={(e) => handleInputChange((e.target as HTMLInputElement).value)}
              onKeyDown={handleKeyDown}
              onFocus={handleInputFocus}
              onClick={handleInputClick}
              placeholder="Search homes by name, address, or phone number..."
              className="h-14 pr-20"
            />
            <div className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center gap-1">
              {inputValue && (
                <button
                  type="button"
                  onClick={handleClear}
                  className="hover:bg-accent rounded p-1 transition-colors"
                  tabIndex={-1}
                >
                  <X className="h-4 w-4 text-muted-foreground" />
                </button>
              )}
              <button
                type="button"
                onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                className="hover:bg-accent rounded p-1 transition-colors"
                tabIndex={-1}
              >
                <ChevronsUpDown className="h-4 w-4 text-muted-foreground" />
              </button>
            </div>
          </div>
        </PopoverTrigger>
        <PopoverContent 
          className="p-0 w-[var(--radix-popover-trigger-width)]" 
          align="start"
          onOpenAutoFocus={(e) => e.preventDefault()}
        >
          <div className="max-h-[300px] overflow-y-auto">
            <div className="p-1">
              {isLoadingHomes ? (
                <div className="py-6 text-center text-sm text-muted-foreground">
                  Loading homes...
                </div>
              ) : filteredOptions.length === 0 ? (
                <div className="py-6 text-center text-sm text-muted-foreground">
                  {inputValue.trim() ? "No homes found matching your search." : "No homes found."}
                </div>
              ) : (
                filteredOptions.map((option) => (
                  <div
                    key={option.value}
                    onClick={() => handleOptionSelect(option)}
                    className="relative flex cursor-pointer select-none items-center rounded-sm px-2 py-2 text-sm outline-none transition-colors hover:bg-accent hover:text-accent-foreground"
                  >
                    <Check className="mr-2 h-4 w-4 opacity-0" />
                    <span className="truncate">{getDisplayLabel(option)}</span>
                  </div>
                ))
              )}
            </div>
          </div>
        </PopoverContent>
      </Popover>
    </div>
  )
}

export default FilterSearchHomeInputCleanV2
